/**
 * Aenderungen fuer die Synchronisierung erfassen (T-04).
 *
 * Eine Dexie-Middleware unterhalb aller Repositories: Jeder Schreibvorgang
 * auf eine gesicherte Tabelle - `add`, `put`, `update`, `bulk*`, `delete`,
 * `clear` - stempelt die geaenderten Felder in `syncFelder` und markiert den
 * Eintrag als offen. Die Repositories muessen davon nichts wissen.
 *
 * Damit das in derselben Transaktion passiert, nimmt jede schreibende
 * Transaktion auf eine gesicherte Tabelle `syncFelder` automatisch mit.
 * Uebernommene Aenderungen vom Server (`alsUebernahme`) werden nicht neu
 * gestempelt. Ohne eingerichtete Synchronisierung (`db.erfassung.aktiv`) laeuft
 * alles wie bisher.
 */
import Dexie, { type DBCore, type DBCoreMutateRequest, type DBCoreMutateResponse, type DBCoreTable, type DBCoreTransaction, type Middleware, type Transaction } from 'dexie';

import { stempeln, type Daten } from '@/domain/sync/zusammenfuehren';
import { syncSchluessel, type SyncFeldStand } from '@/domain/sync/typen';
import type { Uhrwerk } from '@/domain/sync/uhr';

export const FELDER = 'syncFelder';
const MARKE = '__olePrivatSyncUebernahme';

/** Je Datenbank: ist die Synchronisierung eingerichtet, mit welcher Uhr, wer will von Aenderungen hoeren? */
export interface ErfassungsZustand {
  aktiv: boolean;
  uhr: Uhrwerk | null;
  nachAenderung: (() => void) | null;
}

export const neueErfassung = (): ErfassungsZustand => ({ aktiv: false, uhr: null, nachAenderung: null });

/** Schreibvorgaenge dieser Transaktion kommen vom Server - nicht stempeln. */
export function alsUebernahme(tx: Transaction): void {
  (tx.idbtrans as unknown as Record<string, unknown>)[MARKE] = true;
}

const hatFelder = (trans: DBCoreTransaction) =>
  (trans as unknown as IDBTransaction).objectStoreNames?.contains(FELDER) ?? false;

/**
 * Bewusst als `.then`-Kette statt `async`/`await`: Dexie haelt seinen
 * Transaktionskontext nur ueber die eigenen Promises. Ein natives `await`
 * verliert ihn, und die Schichten darunter (Cache, Beobachtung) finden die
 * Transaktion nicht mehr.
 */
export function betroffeneSchluessel(t: DBCoreTable, req: DBCoreMutateRequest): Promise<unknown[]> {
  switch (req.type) {
    case 'add':
    case 'put':
      return Dexie.Promise.resolve(req.keys ?? req.values.map((v) => t.schema.primaryKey.extractKey!(v)));
    case 'delete':
      return Dexie.Promise.resolve(req.keys);
    case 'deleteRange':
      return t.query({ trans: req.trans, query: { index: t.schema.primaryKey, range: req.range }, values: false }).then((r) => r.result);
  }
}

function staendeBerechnen(
  name: string, keys: unknown[], alte: (Daten | undefined)[], neue: (Daten | undefined)[],
  staende: (SyncFeldStand | undefined)[], fehlgeschlagen: Record<number, unknown>, stempel: string,
): SyncFeldStand[] {
  const geaendert: SyncFeldStand[] = [];
  keys.forEach((k, i) => {
    if (fehlgeschlagen[i] || (!alte[i] && !neue[i])) return;
    const vorher = staende[i] ?? { schluessel: syncSchluessel(name, String(k)), tabelle: name, id: String(k), f: {}, bf: {}, seq: 0, offen: 0 as const };
    const { f, x } = stempeln(alte[i], neue[i], vorher, stempel);
    // Nichts geaendert (z. B. dieselben Werte erneut gespeichert): kein Stempel.
    if (x === vorher.x && Object.keys(f).every((feld) => f[feld] === vorher.f[feld])) return;
    const stand: SyncFeldStand = { ...vorher, f, offen: 1 };
    if (x) stand.x = x; else delete stand.x;
    // Neu angelegter oder wiederhergestellter Anhang (F-45): Die Datei auf dem
    // Server kann mit der Loeschung verschwunden sein - also neu hochladen.
    if (name === 'attachments' && !alte[i] && neue[i]) delete stand.datei;
    geaendert.push(stand);
  });
  return geaendert;
}

function erfasst(name: string, t: DBCoreTable, felderTabelle: () => DBCoreTable, erfassung: ErfassungsZustand): DBCoreTable {
  return {
    ...t,
    mutate(req): Promise<DBCoreMutateResponse> {
      const uhr = erfassung.uhr;
      if (!erfassung.aktiv || !uhr || (req.trans as unknown as Record<string, unknown>)[MARKE] || !hatFelder(req.trans)) return t.mutate(req);
      const trans = req.trans;
      const felder = felderTabelle();

      return betroffeneSchluessel(t, req).then((keys) => {
        if (!keys.length) return t.mutate(req);
        const alteHolen: Promise<(Daten | undefined)[]> = req.type === 'add' ? Dexie.Promise.resolve(keys.map(() => undefined)) : t.getMany({ trans, keys });
        return alteHolen.then((alte) => t.mutate(req).then((res) => {
          const neue: (Daten | undefined)[] = req.type === 'add' || req.type === 'put' ? [...req.values] : keys.map(() => undefined);
          const schluessel = keys.map((k) => syncSchluessel(name, String(k)));
          return felder.getMany({ trans, keys: schluessel }).then((staende) => {
            const geaendert = staendeBerechnen(name, keys, alte, neue, staende as (SyncFeldStand | undefined)[], res.failures ?? {}, uhr.tick());
            if (!geaendert.length) return res;
            return felder.mutate({ type: 'put', trans, values: geaendert }).then(() => {
              erfassung.nachAenderung?.();
              return res;
            });
          });
        }));
      });
    },
  };
}

export function syncErfassung(tabellen: ReadonlySet<string>, erfassung: ErfassungsZustand): Middleware<DBCore> {
  return {
    stack: 'dbcore',
    name: 'syncErfassung',
    create(down) {
      return {
        ...down,
        transaction(stores, mode, options) {
          const mit = erfassung.aktiv && mode === 'readwrite' && !stores.includes(FELDER) && stores.some((s) => tabellen.has(s))
            ? [...stores, FELDER]
            : stores;
          return down.transaction(mit, mode, options);
        },
        // `syncFelder` erst bei Bedarf holen: Waehrend einer Versions-Umstellung
        // kennt die Datenbank die Tabelle noch nicht.
        table(name) {
          const t = down.table(name);
          return tabellen.has(name) ? erfasst(name, t, () => down.table(FELDER), erfassung) : t;
        },
      };
    },
  };
}
